import { create } from 'zustand'
import type { VendorDetailItem } from '@/services/vendorDetails'
import { getVendorDetails } from '@/services/vendorDetails'
import {
  EMPTY_VENDOR_TABLE_FILTERS,
  type VendorTableFilters,
} from '@/pages/vendorDetailsFilters'

export const VENDOR_DETAILS_CACHE_KEY = 'bookkeeper:vendor-details-cache'
export const VENDOR_DETAILS_CACHE_TTL_MS = 60 * 60 * 1000

interface CachedPayload {
  savedAt: number
  vendors: VendorDetailItem[]
  ltmStart: string
  ltmEnd: string
  ltmSyncedAt: string | null
  search: string
  tableFilters: VendorTableFilters
}

interface VendorDetailsState {
  vendors: VendorDetailItem[]
  ltmStart: string
  ltmEnd: string
  ltmSyncedAt: string | null
  search: string
  tableFilters: VendorTableFilters
  cachedAt: number | null

  setSearch: (search: string) => void
  setTableFilters: (
    filters: VendorTableFilters | ((prev: VendorTableFilters) => VendorTableFilters)
  ) => void
  clearTableFilters: () => void
  setVendorData: (data: {
    vendors: VendorDetailItem[]
    ltmStart: string
    ltmEnd: string
    ltmSyncedAt: string | null
  }) => void
  isCacheValid: () => boolean
  hasCachedVendors: () => boolean
}

function loadFromStorage(): CachedPayload | null {
  try {
    const raw = localStorage.getItem(VENDOR_DETAILS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedPayload
    if (!parsed?.savedAt || !Array.isArray(parsed.vendors)) return null
    if (Date.now() - parsed.savedAt >= VENDOR_DETAILS_CACHE_TTL_MS) {
      localStorage.removeItem(VENDOR_DETAILS_CACHE_KEY)
      return null
    }
    return parsed
  } catch {
    localStorage.removeItem(VENDOR_DETAILS_CACHE_KEY)
    return null
  }
}

function saveToStorage(payload: CachedPayload): void {
  localStorage.setItem(VENDOR_DETAILS_CACHE_KEY, JSON.stringify(payload))
}

function persistState(state: {
  cachedAt: number | null
  vendors: VendorDetailItem[]
  ltmStart: string
  ltmEnd: string
  ltmSyncedAt: string | null
  search: string
  tableFilters: VendorTableFilters
}): void {
  if (state.cachedAt == null || state.vendors.length === 0) return
  saveToStorage({
    savedAt: state.cachedAt,
    vendors: state.vendors,
    ltmStart: state.ltmStart,
    ltmEnd: state.ltmEnd,
    ltmSyncedAt: state.ltmSyncedAt,
    search: state.search,
    tableFilters: state.tableFilters,
  })
}

const initialCache = loadFromStorage()

export const useVendorDetailsStore = create<VendorDetailsState>((set, get) => ({
  vendors: initialCache?.vendors ?? [],
  ltmStart: initialCache?.ltmStart ?? '',
  ltmEnd: initialCache?.ltmEnd ?? '',
  ltmSyncedAt: initialCache?.ltmSyncedAt ?? null,
  search: initialCache?.search ?? '',
  tableFilters: initialCache?.tableFilters ?? { ...EMPTY_VENDOR_TABLE_FILTERS },
  cachedAt: initialCache?.savedAt ?? null,

  setSearch: (search) => {
    const state = get()
    const next = { ...state, search }
    persistState(next)
    set({ search })
  },

  setTableFilters: (filters) => {
    const state = get()
    const tableFilters =
      typeof filters === 'function' ? filters(state.tableFilters) : filters
    const next = { ...state, tableFilters }
    persistState(next)
    set({ tableFilters })
  },

  clearTableFilters: () => {
    const state = get()
    const tableFilters = { ...EMPTY_VENDOR_TABLE_FILTERS }
    const next = { ...state, tableFilters }
    persistState(next)
    set({ tableFilters })
  },

  setVendorData: ({ vendors, ltmStart, ltmEnd, ltmSyncedAt }) => {
    const cachedAt = Date.now()
    const state = get()
    const next = { ...state, vendors, ltmStart, ltmEnd, ltmSyncedAt, cachedAt }
    persistState(next)
    set({ vendors, ltmStart, ltmEnd, ltmSyncedAt, cachedAt })
  },

  isCacheValid: () => {
    const { cachedAt } = get()
    if (cachedAt == null) return false
    return Date.now() - cachedAt < VENDOR_DETAILS_CACHE_TTL_MS
  },

  hasCachedVendors: () => get().vendors.length > 0,
}))

export async function fetchVendorDetailsIntoStore(refresh = false): Promise<void> {
  const data = await getVendorDetails(refresh)
  useVendorDetailsStore.getState().setVendorData({
    vendors: data.vendors,
    ltmStart: data.ltm_start_date,
    ltmEnd: data.ltm_end_date,
    ltmSyncedAt: data.ltm_stats_synced_at ?? null,
  })
}
